import { Plus, Search, SlidersHorizontal } from "lucide-react"; // Added Search and SlidersHorizontal
import { useCallback, useEffect, useMemo, useRef, useState } from "react"; // Added useRef, useEffect
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../../../src/App.css"; // Importing the CSS file for styles
import StaffattendanceTable from "../../components/AdminPortal/STAFF/tables/StaffattendanceTable";
import StaffworkTable from "../../components/AdminPortal/STAFF/tables/StaffworkTable";
import TopNavbar from "../../components/AdminPortal/TopNavbar";
import { useAuth } from "../../context/AuthContext";
import { useSidebar } from "../../context/SidebarContext";

const ITEMS_PER_PAGE = 5;
const API_DOMAIN = import.meta.env.VITE_CRM_API_URL
  ? new URL(import.meta.env.VITE_CRM_API_URL).origin
  : "https://dashboard.lono.co.in";
const DEFAULT_PROFILE_PIC_WEB_PATH = "/images/profile_icon.png";

const StaffManagement = () => {
  const { sidebarExpanded, isMobile } = useSidebar();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("workPerformance");
  const [selectedStaffMembers, setSelectedStaffMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRoleFilter, setSelectedRoleFilter] = useState("All");
  const [isRoleFilterOpen, setIsRoleFilterOpen] = useState(false);
  const roleFilterRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const performanceSummaries = useMemo(() => [], []); // Memoized empty array for export dummy data

  const exportToExcelAndZip = useCallback(async () => {
    if (selectedStaffMembers.length === 0) {
      toast.error("Please select at least one staff to export.");
      return;
    }

    try {
      setLoading(true);
      const JSZip = (await import("jszip")).default;
      const { saveAs } = await import("file-saver");
      const XLSX = await import("xlsx");

      const zip = new JSZip();

      for (const staffId of selectedStaffMembers) {
        // Try to find summary
        const summary = performanceSummaries.find((s) => s.staffId === staffId);

        const folderName = (summary?.staffName || `Staff_${staffId}`).replace(
          /\s+/g,
          "_"
        );
        const folder = zip.folder(folderName);

        // Prepare the Excel row data
        const excelRow = summary
          ? {
              "STAFF PROFILE": summary.staffName || "N/A",
              ROLE: summary.role || "N/A",
              "TOTAL ASSIGNED": summary.totalTasks || 0,
              "PERFORMANCE RATING":
                summary.performanceRating != null
                  ? `${summary.performanceRating.toFixed(1)} / 5`
                  : "N/A",
              "WORKING HOURS": `${Math.round(summary.workingHours) || 0} / ${
                Math.round(summary.totalExpectedHours) || 0
              } hrs`,
              "TASK PROGRESS": `${summary.tasksCompleted || 0} / ${
                summary.totalTasks || 0
              }`,
              "LEAVES TAKEN": summary.leavesTaken || 0,
              "OVERTIME HOURS": `${Math.round(summary.overtimeHours) || 0} hrs`,
            }
          : {
              "STAFF PROFILE": `Staff ID: ${staffId}`,
              ROLE: "No data",
              "TOTAL ASSIGNED": "No data",
              "PERFORMANCE RATING": "No data",
              "WORKING HOURS": "No data",
              "TASK PROGRESS": "No data",
              "LEAVES TAKEN": "No data",
              "OVERTIME HOURS": "No data",
            };

        const worksheet = XLSX.utils.json_to_sheet([excelRow]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Performance");

        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        folder.file("performance_report.xlsx", excelBuffer);
      }

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "staff_performance_reports.zip");
      toast.success("Staff performance data downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export performance data.");
    } finally {
      setLoading(false);
    }
  }, [selectedStaffMembers, performanceSummaries]);

  const handleAddStaff = useCallback(() => {
    const basePath = user?.role === "admin" ? "/admin" : "/subAdmin";
    navigate(`${basePath}/staff/add`);
  }, [navigate, user?.role]);

  const handleViewStaffProfile = useCallback(
    (staffId) => {
      const basePath = user?.role === "admin" ? "/admin" : "/subAdmin";
      navigate(`${basePath}/staff/${staffId}`);
    },
    [navigate, user?.role]
  );

  const getDisplayProfilePicUrl = useCallback((picturePath) => {
    const defaultUrl = `${API_DOMAIN}${DEFAULT_PROFILE_PIC_WEB_PATH}`;
    if (!picturePath) return defaultUrl;

    if (
      picturePath.startsWith("/") ||
      picturePath.startsWith("http://") ||
      picturePath.startsWith("https://")
    ) {
      return picturePath.startsWith("/")
        ? `${API_DOMAIN}${picturePath}`
        : picturePath;
    }
    if (picturePath === "./public/images/profile_icon.png") {
      return `${API_DOMAIN}/images/profile_icon.png`;
    }
    return defaultUrl;
  }, []);

  const handleSelectAllForPage = useCallback(
    (pageIds) => {
      const allOnPageSelected = pageIds.every((id) =>
        selectedStaffMembers.includes(id)
      );

      setSelectedStaffMembers((prevSelected) => {
        if (allOnPageSelected) {
          return prevSelected.filter((id) => !pageIds.includes(id));
        } else {
          const newSelected = new Set(prevSelected);
          pageIds.forEach((id) => newSelected.add(id));
          return Array.from(newSelected);
        }
      });
    },
    [selectedStaffMembers]
  );

  const handleCheckboxChange = useCallback((id) => {
    setSelectedStaffMembers((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((staffId) => staffId !== id)
        : [...prevSelected, id]
    );
  }, []);

  const mainDivStyle = useMemo(
    () => ({
      marginLeft: isMobile ? "0" : sidebarExpanded ? "200px" : "60px",
      width: isMobile
        ? "100%"
        : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
    }),
    [isMobile, sidebarExpanded]
  );

  const handleRoleFilterChange = (role) => {
    setSelectedRoleFilter(role);
    setIsRoleFilterOpen(false); // Close dropdown after selection
  };

  const roleOptions = [
    { value: "All", label: "Filters" },
    { value: "admin", label: "Admin" },
    { value: "subAdmin", label: "Sub Admin" },
    { value: "staff", label: "Loan Officer" },
    { value: "hr", label: "HR" },
    { value: "marketing", label: "Market Officer" },
    { value: "sales", label: "Sales" },
    { value: "strategy", label: "Strategy" },
    { value: "credit-manager", label: "Credit Manager" },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        roleFilterRef.current &&
        !roleFilterRef.current.contains(event.target)
      ) {
        setIsRoleFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className="min-h-screen bg-black text-white mt-12"
      style={mainDivStyle}
    >
      <TopNavbar />

      <div className="px-4 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
        <h1 className="text-lg text-white">Staff Management</h1>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleAddStaff}
            className="flex items-center gap-2 bg-yellow-400 text-black px-2 py-1 rounded-md font-semibold"
          >
            <Plus size={20} /> {/* Plus icon */}
            Add New Staff
          </button>

          <button
            onClick={exportToExcelAndZip}
            // ADDED THE BACKGROUND COLOR HERE
            className="download-btn"
          >
            Download as ZIP
            <svg
              width="15"
              height="21"
              viewBox="0 0 15 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.2578 4.17188C14.7266 4.64062 15 5.26562 15 5.92969V18C15 19.4062 13.8672 20.5 12.5 20.5H2.5C1.09375 20.5 0 19.4062 0 18V3C0 1.63281 1.09375 0.5 2.5 0.5H9.57031C10.2344 0.5 10.8594 0.773438 11.3281 1.24219L14.2578 4.17188ZM13.125 18V6.75H10C9.29688 6.75 8.75 6.20312 8.75 5.5V2.41406H6.875V3H4.375V2.41406H2.5C2.14844 2.41406 1.875 2.6875 1.875 3.03906V18C1.875 18.3516 2.14844 18.625 2.5 18.625H12.5C12.8125 18.625 13.125 18.3516 13.125 18ZM6.875 4.25V5.5H4.375V4.25H6.875ZM6.875 6.75V8H4.375V6.75H6.875ZM6.875 9.25L8.04688 13.8203C8.35938 15.3438 7.1875 16.75 5.625 16.75C4.02344 16.75 2.85156 15.3438 3.16406 13.8203L4.375 9.25H6.875ZM6.25 14.875C6.5625 14.875 6.875 14.6016 6.875 14.25C6.875 13.9375 6.5625 13.625 6.25 13.625H5C4.64844 13.625 4.375 13.9375 4.375 14.25C4.375 14.6016 4.64844 14.875 5 14.875H6.25Z"
                fill="#FFCA20"
              />
            </svg>
            {/* Download icon, placed after the text */}
          </button>
        </div>
      </div>

      <div className="tab-strip-border overflow-x-auto">
        <div className="flex">
          <button
            onClick={() => setActiveTab("workPerformance")}
            className={`tab-button ${
              activeTab === "workPerformance"
                ? "active-tab-border" // Apply the active border class
                : "inactive-tab" // Apply inactive tab class
            }`}
          >
            Work Performance
          </button>
          <button
            onClick={() => setActiveTab("attendance")}
            className={`tab-button ${
              activeTab === "attendance"
                ? "active-tab-border" // Apply the active border class
                : "inactive-tab" // Apply inactive tab class
            }`}
          >
            Attendance & Tasks
          </button>
        </div>
      </div>

      <div className="p-4">
        {/* Search and Filter Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          {/* Search Input */}
          <div className="relative w-full sm:w-auto sm:flex-1 lg:max-w-xs">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Search size={16} />
            </div>
            <input
              type="text"
              placeholder="Search Staff or Branch Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-black rounded-lg px-10 py-2 text-sm w-full border border-[#333333] focus:ring-[#ffca20] focus:border-[#ffca20]"
            />
          </div>

          {/* Role Filter Dropdown Button */}
          <div
            ref={roleFilterRef}
            className="relative w-full sm:w-auto rounded-lg border border-[#333333]"
          >
            <button
              onClick={() => setIsRoleFilterOpen(!isRoleFilterOpen)}
              className="flex items-center justify-between gap-2 border border-[#333333] bg-transparent px-3 py-2 rounded-lg whitespace-nowrap text-white w-full sm:w-auto hover:border-[#ffca20]"
            >
              <SlidersHorizontal size={16} className="text-[#ffca20]" />
              <span>
                {roleOptions.find((opt) => opt.value === selectedRoleFilter)
                  ?.label || "Filter by Role"}
              </span>
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${
                  isRoleFilterOpen ? "rotate-180" : ""
                }`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                ></path>
              </svg>
            </button>

            {isRoleFilterOpen && (
              <div className="absolute right-0 z-20 mt-2 w-full sm:w-56 bg-[#1e1e1e] p-2 rounded-md shadow-lg border border-[#333333]">
                {roleOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => handleRoleFilterChange(option.value)}
                    className={`px-3 py-2 text-sm rounded-md cursor-pointer hover:bg-[#333333] ${
                      selectedRoleFilter === option.value
                        ? "bg-[#ffca20] text-black"
                        : "text-white"
                    }`}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <p className="text-gray-400 mb-4 text-sm">
          Select staff members to enable bulk actions. <span className="ml-2 text-sm text-blue-400">
    ({selectedStaffMembers.length} selected)
  </span>
        </p>

        {activeTab === "workPerformance" ? (
          <StaffworkTable
            searchTerm={searchTerm}
            selectedRoleFilter={selectedRoleFilter}
            getDisplayProfilePicUrl={getDisplayProfilePicUrl}
            handleViewStaffProfile={handleViewStaffProfile}
            ITEMS_PER_PAGE={ITEMS_PER_PAGE}
            setSelectedStaffMembers={setSelectedStaffMembers}
            selectedStaffMembers={selectedStaffMembers}
            handleCheckboxChange={handleCheckboxChange}
            handleSelectAll={handleSelectAllForPage}
          />
        ) : (
          <StaffattendanceTable
            selectedStaffMembers={selectedStaffMembers}
            setSelectedStaffMembers={setSelectedStaffMembers}
            handleViewStaffProfile={handleViewStaffProfile}
            ITEMS_PER_PAGE={ITEMS_PER_PAGE}
            searchTerm={searchTerm}
            selectedRoleFilter={selectedRoleFilter}
            getDisplayProfilePicUrl={getDisplayProfilePicUrl}
          />
        )}
      </div>
    </div>
  );
};

export default StaffManagement;
